import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { DatasetForm } from '@/components/datasets/DatasetForm';
import { useDatasetForm } from '@/components/datasets/useDatasetForm';
import type { DatasetSummaryDto, DatasetImportResultDto } from '@/api/types';

interface DatasetModalProps {
  mode: 'create' | 'edit';
  dataset?: DatasetSummaryDto | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<DatasetImportResultDto>;
  onSuccess: () => void;
}

export function DatasetModal({ mode, dataset, open, onClose, onSubmit, onSuccess }: DatasetModalProps) {
  const { formData, error, busy, setField, setFile, reset, submit } = useDatasetForm({
    mode,
    initialDataset: mode === 'edit' ? dataset : null,
    onSubmit,
    onSuccess,
  });

  const handleSave = async () => {
    const result = await submit();
    if (result.success) {
      reset();
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!open) return null;

  const title = mode === 'create' ? 'Create Dataset' : 'Edit Dataset';
  const submitLabel = mode === 'create' ? 'Create' : 'Save';

  const isEdit = mode === 'edit';

  return (
    <Dialog
      title={title}
      onClose={handleCancel}
      open={open}
      actions={
        <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSave} disabled={busy}>
            {busy ? 'Processing…' : submitLabel}
          </Button>
        </div>
      }
    >
      <DatasetForm
        formData={formData}
        error={error}
        setField={setField}
        setFile={setFile}
        isEdit={isEdit}
      />
    </Dialog>
  );
}
